import React, { useState, useEffect } from 'react';
import QuotationHeader from './Quotation_Component/QuotationHeader';
import QuotationFilters from './Quotation_Component/QuotationFilters';
import QuotationTable from './Quotation_Component/QuotationTable';
import NewQuotationModal from './Quotation_Component/NewQuotationModal';
import ViewQuotationModal from './Quotation_Component/ViewQuotationModal';
import NotificationContainer from './Quotation_Component/NotificationContainer';
import ConfirmDialog from './Quotation_Component/ConfirmDialog';
import apiService from '../services/api';
import { useNavigate } from 'react-router-dom';


const QuotationPage = () => {
    const [quotations, setQuotations] = useState([]);
    const [filteredQuotations, setFilteredQuotations] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('desc');
    const [pagination, setPagination] = useState({ page: 1, total: 0, limit: 10 });
    const [notifications, setNotifications] = useState([]);
    const [products, setProducts] = useState([]);
    const [productCategories, setProductCategories] = useState({});
    const [user, setUser] = useState(null);
    const navigate = useNavigate();


    // Modal states
    const [showNewModal, setShowNewModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedQuotation, setSelectedQuotation] = useState(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [quotationToDelete, setQuotationToDelete] = useState(null);


    useEffect(() => {
    loadQuotations();
    loadCurrentUser();
}, []);

const loadCurrentUser = async () => {
    try {
        const syncUser = apiService.getUserSync && apiService.getUserSync();
        if (syncUser) {
            setUser(syncUser);
            return;
        }
        const response = await apiService.getCurrentUser();
        if (response.success && response.user) {
            setUser(response.user);
        } else if (response.needsLogin) {
            apiService.clearUserData && apiService.clearUserData();
            navigate('/login');
        } else {
            navigate('/login');
        }
    } catch (error) {
        if (!(apiService.getUserSync && apiService.getUserSync())) {
            navigate('/login');
        }
    }
};



    // Filter and sort quotations
    useEffect(() => {
        let filtered = quotations.filter(q =>
            (q.quotationNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (q.clientName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (q.projectName || '').toLowerCase().includes(searchTerm.toLowerCase())
        );

        // Sort
        filtered.sort((a, b) => {
            let aVal = a[sortBy];
            let bVal = b[sortBy];

            if (sortBy === 'createdAt' || sortBy === 'updatedAt') {
                aVal = new Date(aVal);
                bVal = new Date(bVal);
            }

            if (sortOrder === 'asc') {
                return aVal > bVal ? 1 : -1;
            } else {
                return aVal < bVal ? 1 : -1;
            }
        });

        setFilteredQuotations(filtered);
        setPagination(prev => ({ ...prev, total: filtered.length }));
    }, [quotations, searchTerm, sortBy, sortOrder]);

    const loadQuotations = async () => {
        setIsLoading(true);
        try {
            // Call your backend API to get quotations
            const response = await apiService.getQuotations(); // Make sure this method exists in your apiService
            if (response.success) {
                setQuotations(response.data);
                addNotification('Quotations loaded successfully', 'success');
            } else {
                addNotification('Failed to load quotations', 'error');
            }
        } catch (error) {
            addNotification('Failed to load quotations', 'error');
            console.error('Failed to load quotations:', error);
        } finally {
            setIsLoading(false);
        }
    };

const loadProducts = async () => {
    console.log('🚀 Loading ALL products (no limits!)...');
    try {
        const response = await apiService.getQuotationProducts({
            limit: 100000 // YOLO! Load everything! 🔥
        });
        
        console.log('📦 Products API response:', response);
        
        if (response.success) {
            setProducts(response.data);
            console.log(`🎉 BOOM! Loaded ${response.data.length} products from ${response.collectionsQueried} categories`);
            console.log(`📊 Total available: ${response.pagination.total}`);
            console.log('🔥 Sample products:', response.data.slice(0, 5));
            
            addNotification(
                `🎉 Loaded ${response.data.length} products from ${response.successfulCollections} categories!`, 
                'success'
            );
        } else {
            console.error('❌ Products API returned success: false', response);
            addNotification('Failed to load products', 'error');
        }
    } catch (error) {
        console.error('💥 Failed to load products:', error);
        addNotification('Failed to load products: ' + error.message, 'error');
    }
};

    const loadProductCategories = async () => {
        try {
            const response = await apiService.getProductCategories();
            if (response.success) {
                setProductCategories(response.data);
            }
        } catch (error) {
            console.error('Failed to load categories:', error);
        }
    };



    const addNotification = (message, type = 'info') => {
        const id = Date.now();
        setNotifications(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setNotifications(prev => prev.filter(n => n.id !== id));
        }, 5000);
    };

const handleCreateOrUpdateQuotation = async (quotationData) => {
    setIsLoading(true);
    try {
        let response;
        if (quotationData._id || quotationData.id) {
            // Update existing
            const id = quotationData._id || quotationData.id;
            response = await apiService.updateQuotation(id, quotationData);
        } else {
            // Create new
            response = await apiService.createQuotation(quotationData);
        }
        if (response.success) {
            // Reload quotations from backend to reflect changes
            await loadQuotations();
            setShowNewModal(false);
            setSelectedQuotation(null);
            addNotification(
                quotationData._id || quotationData.id
                    ? 'Quotation updated successfully'
                    : 'Quotation created successfully',
                'success'
            );
        } else {
            addNotification('Failed to save quotation', 'error');
        }
    } catch (error) {
        addNotification('Failed to save quotation', 'error');
    } finally {
        setIsLoading(false);
    }
};

    const handleDeleteQuotation = async (id) => {
        setIsLoading(true);
        try {
            const response = await apiService.deleteQuotation(id);
            if (response.success) {
                await loadQuotations(); // Reload from backend
                setShowConfirmDialog(false);
                setQuotationToDelete(null);
                addNotification('Quotation deleted successfully', 'success');
            } else {
                addNotification('Failed to delete quotation', 'error');
            }
        } catch (error) {
            addNotification('Failed to delete quotation', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleViewQuotation = (quotation) => {
        setSelectedQuotation(quotation);
        setShowViewModal(true);
    };

    const handleEditQuotation = (quotation) => {
        setSelectedQuotation(quotation);
        setShowNewModal(true);
    };

    const confirmDelete = (quotation) => {
        setQuotationToDelete(quotation);
        setShowConfirmDialog(true);
    };

    const generateQuotationNumber = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const count = quotations.length + 1;
        return `Q-${year}-${month}-${String(count).padStart(3, '0')}`;
    };

    const getStats = () => {
        const total = quotations.length;
        const approved = quotations.filter(q => q.status === 'approved').length;
        const pending = quotations.filter(q => q.status === 'pending').length;
        const revenue = quotations
            .filter(q => q.status === 'approved')
            .reduce((sum, q) => sum + q.totalAmount, 0);

        return { total, approved, pending, revenue };
    };

    const paginatedQuotations = filteredQuotations.slice(
        (pagination.page - 1) * pagination.limit,
        pagination.page * pagination.limit
    );

    if (!user) {
    return (
        <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>
            <p>Loading...</p>
        </div>
    );
}

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: '#f8fafc',
            padding: '24px'
        }}>
            <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                <QuotationHeader
                    stats={getStats()}
                    onNewQuotation={() => setShowNewModal(true)}
                />

                <QuotationFilters
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                    sortBy={sortBy}
                    onSortByChange={setSortBy}
                    sortOrder={sortOrder}
                    onSortOrderChange={setSortOrder}
                />

                <QuotationTable
                    quotations={paginatedQuotations}
                    isLoading={isLoading}
                    pagination={pagination}
                    onPaginationChange={setPagination}
                    onView={handleViewQuotation}
                    onEdit={handleEditQuotation}
                    onDelete={confirmDelete}
                />
            </div>

            {showNewModal && (
                <NewQuotationModal
                    quotation={selectedQuotation}
                    onClose={() => {
                        setShowNewModal(false);
                        setSelectedQuotation(null);
                    }}
                    onSave={handleCreateOrUpdateQuotation}
                    products={products}
                    productCategories={productCategories}
                />
            )}

            {showViewModal && selectedQuotation && (
                <ViewQuotationModal
                    quotation={selectedQuotation}
                    onClose={() => {
                        setShowViewModal(false);
                        setSelectedQuotation(null);
                    }}
                />
            )}

            {showConfirmDialog && quotationToDelete && (
                <ConfirmDialog
                    title="Delete Quotation"
                    message={`Are you sure you want to delete quotation ${quotationToDelete.quotationNumber}? This action cannot be undone.`}
                    onConfirm={() => handleDeleteQuotation(quotationToDelete._id || quotationToDelete.id)}
                    onCancel={() => {
                        setShowConfirmDialog(false);
                        setQuotationToDelete(null);
                    }}
                />
            )}

            <NotificationContainer notifications={notifications} />
        </div>
    );
};

export default QuotationPage;